import { Radio, ArrowClockwise, Broadcast, Warning, ShieldWarning, Play } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { timeAgo } from "@/api";

export function ScannerBar({ status, onRefresh, isRefreshing }) {
  const scanningAny = status ? Object.values(status.scanning || {}).some(Boolean) : false;
  const count = status?.symbols_count ?? 0;
  const active = status?.active_signals ?? 0;
  const lastScanMap = status?.last_scan_at || {};
  // Most recent scan across modes
  const lastScan = Object.values(lastScanMap).filter(Boolean).sort().slice(-1)[0] || null;
  const totalScans = status?.total_scans || {};
  const totalScansSum = Object.values(totalScans).reduce((a, b) => a + (b || 0), 0);
  const errors = status?.errors ?? 0;
  const ks = status?.kill_switch || {};
  const risk = status?.risk_stats || {};

  const resetKillSwitch = async () => {
    try {
      await api.killSwitchReset();
      toast.success("Kill switch cleared — scanner resuming");
    } catch {
      toast.error("Failed to reset kill switch");
    }
  };

  return (
    <div
      data-testid="scanner-bar"
      className="border border-white/10 bg-[#0f0f0f] px-4 md:px-6 py-3 flex items-center gap-4 md:gap-8 flex-wrap"
    >
      <div className="flex items-center gap-3">
        <span className="relative flex h-2.5 w-2.5">
          <span className={`pulse-dot absolute inline-flex h-full w-full rounded-full bg-[#00FF66] ${scanningAny ? "" : "opacity-70"}`}></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#00FF66]"></span>
        </span>
        <div className="flex flex-col leading-tight">
          <span className="font-display text-sm font-bold tracking-widest uppercase text-white">
            {scanningAny ? "Scanning Live" : "Idle"}
          </span>
          <span className="font-mono text-[11px] text-white/50">
            {lastScan
              ? `Last scan: ${timeAgo(lastScan)}`
              : count > 0
                ? `Warming scanners · ${count} pairs · first pass in progress`
                : "Bootstrapping…"}
          </span>
        </div>
      </div>

      <div className="hidden md:flex items-center gap-6 font-mono text-[12px]">
        <div className="flex flex-col">
          <span className="text-white/40 uppercase text-[10px] tracking-widest">Pairs</span>
          <span data-testid="tracked-count" className="text-white text-base">{count}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-white/40 uppercase text-[10px] tracking-widest">Active Signals</span>
          <span data-testid="active-signal-count" className="text-[#00FF66] text-base">{active}</span>
        </div>
        <div className="flex flex-col" title={`scalp ${totalScans.scalp || 0} · swing ${totalScans.swing || 0} · position ${totalScans.position || 0}`}>
          <span className="text-white/40 uppercase text-[10px] tracking-widest">Total Scans</span>
          <span className="text-white text-base">{totalScansSum}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          {["scalp", "swing", "position"].map((m) => (
            <span
              key={m}
              data-testid={`mode-indicator-${m}`}
              className={`px-1.5 py-[2px] border uppercase tracking-widest ${
                status?.scanning?.[m]
                  ? "border-[#00FF66]/50 text-[#00FF66] bg-[#00FF66]/10"
                  : "border-white/10 text-white/50"
              }`}
              title={`${m} · ${totalScans[m] || 0} scans`}
            >
              {m[0]}
            </span>
          ))}
        </div>
        {errors > 0 && (
          <div className="flex items-center gap-1 text-[#FF3B30]">
            <Warning size={14} weight="fill" />
            <span className="text-xs">{errors} errors</span>
          </div>
        )}
        {ks.paused && (
          <div
            data-testid="kill-switch-banner"
            className="flex items-center gap-1.5 border border-[#FF3B30]/50 bg-[#FF3B30]/10 px-2 py-1 text-[#FF3B30]"
          >
            <ShieldWarning size={12} weight="fill" />
            <span className="text-[10px] font-mono uppercase tracking-widest">
              Kill Switch · {ks.reason}
            </span>
            <button
              data-testid="reset-kill-switch"
              onClick={resetKillSwitch}
              className="ml-1 text-white/70 hover:text-white"
              title="Resume scanning"
            >
              <Play size={11} weight="fill" />
            </button>
          </div>
        )}
        {status?.news_blackout?.active && (
          <div
            data-testid="news-blackout-banner"
            className="flex items-center gap-1.5 border border-[#F5C518]/50 bg-[#F5C518]/10 px-2 py-1 text-[#F5C518]"
          >
            <ShieldWarning size={12} weight="fill" />
            <span className="text-[10px] font-mono uppercase tracking-widest">
              News Blackout · {status.news_blackout.event?.label}
            </span>
          </div>
        )}
        {(risk.skipped_correlated > 0 || risk.leverage_clamped > 0) && (
          <div className="hidden lg:flex items-center gap-2 text-[10px] font-mono text-white/40">
            <span title="Signals blocked by correlation guard">
              Corr-skip <span className="text-[#F5C518]">{risk.skipped_correlated}</span>
            </span>
            <span title="Trades where leverage was capped by liquidation safety">
              Lev-clamp <span className="text-[#F5C518]">{risk.leverage_clamped}</span>
            </span>
          </div>
        )}
      </div>

      <div className="ml-auto flex items-center gap-3">
        <div className="hidden lg:flex items-center gap-2 text-[11px] font-mono text-white/40">
          <Broadcast size={12} />
          <span>OKX USDT-Perp · WEEX/BLOFIN mapped</span>
        </div>
        <Button
          data-testid="refresh-scanner-btn"
          variant="outline"
          size="sm"
          className="rounded-none border-white/15 bg-transparent hover:bg-white/5 text-white font-mono text-[11px] uppercase tracking-widest"
          onClick={onRefresh}
          disabled={isRefreshing}
        >
          <ArrowClockwise size={12} className={isRefreshing ? "animate-spin mr-1.5" : "mr-1.5"} />
          Refresh Universe
        </Button>
      </div>
    </div>
  );
}
